/**
 * Monaco Editor setup for BPMN DSL v2
 *
 * Features:
 *  - Syntax highlighting for BPMN DSL v2 ("bpmn-dsl" language)
 *  - Autocomplete: lane keywords, element types, connections
 *  - Systems registry autocomplete on "<<" trigger (from /systems)
 *  - Warning decorations for lanes without "<<SystemCode>>" annotation
 */

import { collectBpmnPublishLintDiagnostics } from './publishLintDiagnostics';

// ─── Registry cache ──────────────────────────────────────────────────────────

let bpmnRegistryCache = new Map(); // code -> { name, ... }

/** Call this to update the registry data used for << autocomplete */
export const updateBpmnRegistryEntities = (entitiesMap) => {
  bpmnRegistryCache = entitiesMap instanceof Map ? entitiesMap : new Map(Object.entries(entitiesMap));
};

// ─── Token types (Monarch) ───────────────────────────────────────────────────

const TASK_TYPES = [
  'Task', 'Task.User', 'Task.Service', 'Task.Send', 'Task.Receive',
  'Task.Manual', 'Task.Script', 'Task.Business',
];
const GATEWAY_TYPES = [
  'Gateway', 'Gateway.Exclusive', 'Gateway.Parallel',
  'Gateway.Inclusive', 'Gateway.Event',
];
const EVENT_TYPES = [
  'Start', 'Start.Message', 'Start.Timer', 'Start.Conditional', 'Start.Signal',
  'End', 'End.Message', 'End.Error', 'End.Escalation', 'End.Signal', 'End.Cancel', 'End.Terminate',
  'Event.Message', 'Event.Timer', 'Event.Conditional', 'Event.Signal', 'Event.Link',
  'Throw.Message', 'Throw.Signal', 'Throw.Escalation', 'Throw.Link',
  'Boundary.Timer', 'Boundary.Timer.NI', 'Boundary.Error', 'Boundary.Message',
  'Boundary.Message.NI', 'Boundary.Escalation', 'Boundary.Escalation.NI',
  'Boundary.Signal', 'Boundary.Signal.NI', 'Boundary.Compensation', 'Boundary.Cancel',
];
const SUBPROCESS_TYPES = ['Subprocess', 'CallActivity', 'Subprocess.Transaction'];
const ARTIFACT_TYPES = ['Annotation', 'Group', 'Data.Object', 'Data.Store'];

const ALL_ELEMENT_TYPES = [
  ...TASK_TYPES, ...GATEWAY_TYPES, ...EVENT_TYPES,
  ...SUBPROCESS_TYPES, ...ARTIFACT_TYPES,
];

// ─── Language registration ───────────────────────────────────────────────────

export const setupBpmnDslLanguage = (monaco) => {
  if (window.__bpmnDslRegistered) return;
  window.__bpmnDslRegistered = true;

  monaco.languages.register({ id: 'bpmn-dsl' });

  // ── Monarch tokenizer ────────────────────────────────────────────────────
  monaco.languages.setMonarchTokensProvider('bpmn-dsl', {
    keywords: ['diagram', 'lane', 'labels', 'flow'],
    elementTypes: ALL_ELEMENT_TYPES,

    tokenizer: {
      root: [
        // Single-line comments
        [/\/\/.*$/, 'comment'],

        // diagram "Name"
        [/\bdiagram\b/, 'keyword.diagram', '@diagram_name'],

        // lane "Name" <<SystemCode>> {
        [/\blane\b/, 'keyword.lane', '@lane_header'],

        // labels { ... }
        [/\blabels\b/, 'keyword'],

        // Element types — order matters: longer matches first
        ...ALL_ELEMENT_TYPES
          .slice()
          .sort((a, b) => b.length - a.length)
          .map(t => [new RegExp(`\\b${t.replace('.', '\\.')}\\b`), 'type.element']),

        // Connections -> and -->
        [/-->/, 'operator.assoc'],
        [/->/, 'operator.flow'],

        // Colon (label separator in connections)
        [/:/, 'delimiter'],

        // = assignment
        [/=/, 'delimiter.assign'],

        // String literals
        [/"[^"]*"/, 'string'],

        // <<SystemCode>> annotation
        [/<<[^>]*>>/, 'annotation.system'],

        // Braces
        [/[{}]/, 'delimiter.curly'],

        // Identifiers (variable names, dotted)
        [/[a-zA-Z_][a-zA-Z0-9_]*(\.[a-zA-Z_][a-zA-Z0-9_]*)?/, 'identifier'],

        // Whitespace
        [/\s+/, 'white'],
      ],

      diagram_name: [
        [/"[^"]*"/, 'string', '@pop'],
        [/\s+/, 'white'],
        [/./, { token: '@rematch', next: '@pop' }],
      ],

      lane_header: [
        [/"[^"]*"/, 'string'],
        [/<<([^>]*)>>/, 'annotation.system'],
        [/\{/, 'delimiter.curly', '@pop'],
        [/\s+/, 'white'],
        [/./, { token: '@rematch', next: '@pop' }],
      ],
    },
  });

  // ── Theme token colors ───────────────────────────────────────────────────
  // Light + dark compatible via CSS variables (Monaco handles theme scoping)
  monaco.editor.defineTheme('bpmn-dsl-light', {
    base: 'vs',
    inherit: true,
    rules: [
      { token: 'comment',           foreground: '6A9955', fontStyle: 'italic' },
      { token: 'keyword.diagram',   foreground: '0000FF', fontStyle: 'bold' },
      { token: 'keyword.lane',      foreground: '0070C1', fontStyle: 'bold' },
      { token: 'keyword',           foreground: '0070C1' },
      { token: 'type.element',      foreground: '7B3F9E', fontStyle: 'bold' },
      { token: 'operator.flow',     foreground: 'D66200', fontStyle: 'bold' },
      { token: 'operator.assoc',    foreground: 'D66200' },
      { token: 'string',            foreground: 'A31515' },
      { token: 'annotation.system', foreground: '117900', fontStyle: 'bold' },
      { token: 'delimiter.assign',  foreground: '444444' },
      { token: 'identifier',        foreground: '001080' },
    ],
    colors: {},
  });

  monaco.editor.defineTheme('bpmn-dsl-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { token: 'comment',           foreground: '5C6370', fontStyle: 'italic' },
      { token: 'keyword.diagram',   foreground: '569CD6', fontStyle: 'bold' },
      { token: 'keyword.lane',      foreground: '4FC1FF', fontStyle: 'bold' },
      { token: 'keyword',           foreground: '4FC1FF' },
      { token: 'type.element',      foreground: 'C792EA', fontStyle: 'bold' },
      { token: 'operator.flow',     foreground: 'F78C6C', fontStyle: 'bold' },
      { token: 'operator.assoc',    foreground: 'F78C6C' },
      { token: 'string',            foreground: 'C3E88D' },
      { token: 'annotation.system', foreground: '89DDFF', fontStyle: 'bold' },
      { token: 'delimiter.assign',  foreground: 'AAAAAA' },
      { token: 'identifier',        foreground: 'EEFFFF' },
    ],
    colors: {
      'editor.background': '#1f2124',
    },
  });

  // ── Autocomplete ─────────────────────────────────────────────────────────
  // triggerCharacters includes every printable ASCII char so the popup
  // appears immediately on the first keystroke (Monaco also fires the
  // provider when the user types any char NOT in the list, but only after
  // a 400 ms delay — listing them here makes it instant).
  const TRIGGER_CHARS = [
    ...'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ',
    ...'0123456789',
    '<', '"', '=', ' ', '.', '_', ':', '-', '{',
  ];

  monaco.languages.registerCompletionItemProvider('bpmn-dsl', {
    triggerCharacters: TRIGGER_CHARS,
    provideCompletionItems: (model, position) => {
      const line = model.getLineContent(position.lineNumber);
      const textBefore = line.substring(0, position.column - 1);
      const word = model.getWordUntilPosition(position).word || '';
      const lc = word.toLowerCase();
      const Kind = monaco.languages.CompletionItemKind;
      const SnipRule = monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet;
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: position.column - word.length,
        endColumn: position.column,
      };

      const suggestions = [];

      // ── Trigger: "<<" — show system codes from registry ─────────────────
      const isAfterDblAngle = /<<[^>]*$/.test(textBefore);
      if (isAfterDblAngle) {
        const typedCode = textBefore.match(/<<([^>]*)$/)?.[1] || '';
        const lcTyped = typedCode.toLowerCase();

        // Detect if we are on a lane line and find the quoted name range
        // Pattern: lane "Some Name" <<...
        const laneQuoteMatch = line.match(/^(\s*lane\s+")([^"]*)(")(\s*<<)/);
        // laneQuoteMatch[2] = current text between quotes, positions:
        // quote opens at laneQuoteMatch[1].length (0-based), closes before laneQuoteMatch[3]
        let laneNameEditRange = null;
        if (laneQuoteMatch) {
          const openQuoteCol = laneQuoteMatch[1].length; // 0-based col of char after opening "
          const closeQuoteCol = openQuoteCol + laneQuoteMatch[2].length; // 0-based col of closing "
          laneNameEditRange = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: openQuoteCol + 1,       // Monaco columns are 1-based
            endColumn: closeQuoteCol + 1,
          };
        }

        if (bpmnRegistryCache.size > 0) {
          bpmnRegistryCache.forEach((entity, code) => {
            if (!lcTyped || code.toLowerCase().startsWith(lcTyped) || entity.name?.toLowerCase().includes(lcTyped)) {
              const systemName = entity.name || code;
              const item = {
                label: { label: code, description: systemName },
                kind: Kind.Value,
                detail: systemName,
                documentation: {
                  value: [
                    `**${systemName}** \`${code}\``,
                    entity.description ? `\n\n${entity.description}` : '',
                    entity.owner ? `\n\n_Owner: ${entity.owner}_` : '',
                  ].join(''),
                },
                insertText: code + '>>',
                range: {
                  startLineNumber: position.lineNumber,
                  endLineNumber: position.lineNumber,
                  startColumn: position.column - typedCode.length,
                  endColumn: position.column,
                },
                sortText: `0_${code}`,
              };

              // If we're on a lane line — also replace the name in quotes
              if (laneNameEditRange) {
                item.additionalTextEdits = [
                  {
                    range: laneNameEditRange,
                    text: systemName,
                  },
                ];
              }

              suggestions.push(item);
            }
          });
        } else {
          // No data yet — helpful hint
          suggestions.push({
            label: '(loading systems...)',
            kind: Kind.Text,
            insertText: '',
            range,
            sortText: 'zzz',
          });
        }

        return { suggestions };
      }

      // ── Trigger: lane "..." — suggest adding <<SystemCode>> ───────────────
      if (/^\s*lane\s+"[^"]+"\s*$/.test(textBefore)) {
        // Find the quoted name on this line to build an additionalTextEdit
        const laneQuoteMatch2 = line.match(/^(\s*lane\s+")([^"]*)(")/);
        let laneNameEditRange2 = null;
        if (laneQuoteMatch2) {
          const openQuoteCol = laneQuoteMatch2[1].length;
          const closeQuoteCol = openQuoteCol + laneQuoteMatch2[2].length;
          laneNameEditRange2 = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: openQuoteCol + 1,
            endColumn: closeQuoteCol + 1,
          };
        }

        suggestions.push({
          label: '<<SystemCode>>',
          kind: Kind.Snippet,
          insertText: '<<${1:SYSTEM_CODE}>>',
          insertTextRules: SnipRule,
          detail: 'Link lane to a system',
          documentation: 'Annotate this lane with a system code from the registry',
          range,
          sortText: '0_system',
        });

        bpmnRegistryCache.forEach((entity, code) => {
          const systemName = entity.name || code;
          const item = {
            label: { label: `<<${code}>>`, description: systemName },
            kind: Kind.Value,
            detail: systemName,
            insertText: `<<${code}>>`,
            range,
            sortText: `1_${code}`,
          };
          if (laneNameEditRange2) {
            item.additionalTextEdits = [
              { range: laneNameEditRange2, text: systemName },
            ];
          }
          suggestions.push(item);
        });
        return { suggestions };
      }

      // ── Context: after "=" — element types ───────────────────────────────
      if (/=\s*\S*$/.test(textBefore)) {
        TASK_TYPES.forEach(t => suggestions.push(makeElementSuggestion(t, 'Task', Kind.Class, SnipRule, range)));
        GATEWAY_TYPES.forEach(t => suggestions.push(makeElementSuggestion(t, 'Gateway', Kind.Enum, SnipRule, range)));
        EVENT_TYPES.forEach(t => suggestions.push(makeElementSuggestion(t, 'Event', Kind.Event, SnipRule, range)));
        SUBPROCESS_TYPES.forEach(t => suggestions.push(makeElementSuggestion(t, 'Subprocess', Kind.Module, SnipRule, range)));
        ARTIFACT_TYPES.forEach(t => suggestions.push(makeElementSuggestion(t, 'Artifact', Kind.Variable, SnipRule, range)));
        return { suggestions: word ? suggestions.filter(s => s.label.toLowerCase().startsWith(lc)) : suggestions };
      }

      // ── Context: connection (-> or -->) ───────────────────────────────────
      if (/->|-->/.test(textBefore)) {
        const allVarNames = collectVarNames(model.getValue());
        allVarNames.forEach(v => {
          suggestions.push({
            label: v,
            kind: Kind.Reference,
            insertText: v,
            range,
            sortText: `1_${v}`,
          });
        });
        // Optional label after colon
        suggestions.push({
          label: ': label',
          kind: Kind.Snippet,
          insertText: ': ${1:label}',
          insertTextRules: SnipRule,
          detail: 'Add flow label',
          range,
          sortText: 'z_label',
        });
        return { suggestions: word ? suggestions.filter(s => s.label.toLowerCase().startsWith(lc)) : suggestions };
      }

      // ── Top-level / inside lane: keyword suggestions ──────────────────────
      const isInsideLane = detectInsideLane(model.getValue(), position.lineNumber);

      if (!isInsideLane) {
        // Top-level
        suggestions.push(
          {
            label: 'diagram',
            kind: Kind.Keyword,
            insertText: 'diagram "${1:Process Name}"\n\n',
            insertTextRules: SnipRule,
            detail: 'Diagram header',
            range,
          },
          {
            label: 'lane',
            kind: Kind.Keyword,
            insertText: 'lane "${1:Lane Name}" {\n\t${2}\n}',
            insertTextRules: SnipRule,
            detail: 'Lane block',
            range,
          },
          {
            label: 'lane with system',
            kind: Kind.Snippet,
            insertText: 'lane "${1:Lane Name}" <<${2:SYSTEM_CODE}>> {\n\t${3}\n}',
            insertTextRules: SnipRule,
            detail: 'Lane linked to a system',
            range,
          },
          {
            label: 'labels',
            kind: Kind.Keyword,
            insertText: 'labels {\n\t${1:varName} = "${2:Label}"\n}',
            insertTextRules: SnipRule,
            detail: 'Labels override block',
            range,
          },
        );
      } else {
        // Inside lane: element declarations + connections
        suggestions.push(
          {
            label: 'varName = Type',
            kind: Kind.Snippet,
            insertText: '${1:varName} = ${2:Task} "${3:Label}"',
            insertTextRules: SnipRule,
            detail: 'Element declaration',
            range,
          },
          {
            label: 'from -> to',
            kind: Kind.Snippet,
            insertText: '${1:from} -> ${2:to}',
            insertTextRules: SnipRule,
            detail: 'Sequence flow',
            range,
          },
          {
            label: 'from -> to: label',
            kind: Kind.Snippet,
            insertText: '${1:from} -> ${2:to}: ${3:label}',
            insertTextRules: SnipRule,
            detail: 'Sequence flow with label',
            range,
          },
        );
      }

      // Common: all variable names in document for navigation
      const allVarNames = collectVarNames(model.getValue());
      allVarNames.forEach(v => {
        if (!suggestions.find(s => s.label === v)) {
          suggestions.push({
            label: v,
            kind: Kind.Reference,
            insertText: v,
            range,
            sortText: `z_${v}`,
          });
        }
      });

      return { suggestions: word ? suggestions.filter(s => s.label.toLowerCase().startsWith(lc)) : suggestions };
    },
  });

  // ── Hover provider ────────────────────────────────────────────────────────
  monaco.languages.registerHoverProvider('bpmn-dsl', {
    provideHover: (model, position) => {
      const word = model.getWordAtPosition(position);
      if (!word) return null;
      const token = word.word;

      // Check if it's a known element type
      const elType = ALL_ELEMENT_TYPES.find(t => t === token || t.startsWith(token + '.'));
      if (elType) {
        return {
          range: new monaco.Range(position.lineNumber, word.startColumn, position.lineNumber, word.endColumn),
          contents: [
            { value: `**${elType}**` },
            { value: getElementTypeDescription(elType) },
          ],
        };
      }

      // Check if it's a registry code
      const entity = bpmnRegistryCache.get(token);
      if (entity) {
        return {
          range: new monaco.Range(position.lineNumber, word.startColumn, position.lineNumber, word.endColumn),
          contents: [
            { value: `**${entity.name}** _(${token})_` },
            { value: entity.description || '' },
            { value: entity.owner ? `Owner: ${entity.owner}` : '' },
          ].filter(c => c.value),
        };
      }

      return null;
    },
  });
};

// ─── Diagnostic decorations ──────────────────────────────────────────────────

const bpmnSeverityToMarkerSeverity = (severity, monaco) => {
  if (severity === 'error') return monaco.MarkerSeverity.Error;
  if (severity === 'warning') return monaco.MarkerSeverity.Warning;
  if (severity === 'info') return monaco.MarkerSeverity.Hint;
  return monaco.MarkerSeverity.Info;
};

/**
 * Compute diagnostic markers for the BPMN DSL v2 code.
 * Returns an array of monaco.editor.IMarkerData.
 */
export const computeBpmnDiagnostics = (code, monaco) => {
  if (!code || !monaco) return [];

  const lines = code.split('\n');
  return collectBpmnPublishLintDiagnostics(code).map((diag) => {
    const lineNumber = diag.line || 1;
    const line = lines[lineNumber - 1] || '';
    return {
      severity: bpmnSeverityToMarkerSeverity(diag.severity, monaco),
      message: diag.message,
      startLineNumber: lineNumber,
      startColumn: 1,
      endLineNumber: lineNumber,
      endColumn: line.length + 1,
      source: 'BPMN DSL',
      code: diag.ruleId,
    };
  });
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Collect all variable names declared in the DSL source */
const collectVarNames = (src) => {
  const names = new Set();
  const lines = src.split('\n');
  const assignRe = /^\s*([a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)?)\s*=/;
  lines.forEach(l => {
    const m = l.match(assignRe);
    if (m && !['diagram', 'lane', 'labels'].includes(m[1])) {
      names.add(m[1]);
    }
  });
  return [...names];
};

/** Detect if the given 1-based lineNumber is inside a lane block */
const detectInsideLane = (src, lineNumber) => {
  const lines = src.split('\n');
  let depth = 0;
  let inLane = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isLaneLine = /^\s*lane\s+"/.test(line);
    const opens = (line.match(/\{/g) || []).length;
    const closes = (line.match(/\}/g) || []).length;

    if (isLaneLine && opens > 0) {
      inLane = true;
    }

    depth += opens - closes;

    if (i + 1 === lineNumber) {
      return inLane && depth > 0;
    }

    if (depth <= 0) {
      inLane = false;
      depth = 0;
    }
  }

  return false;
};

const makeElementSuggestion = (type, category, kind, SnipRule, range) => ({
  label: type,
  kind,
  insertText: `${type} "\${1:Label}"`,
  insertTextRules: SnipRule,
  detail: `${category} element`,
  documentation: getElementTypeDescription(type),
  range,
  sortText: `e_${type}`,
});

const getElementTypeDescription = (type) => {
  const descriptions = {
    'Start': 'Start event — begins the process',
    'End': 'End event — terminates the process',
    'End.Error': 'End event with error — terminates with an error',
    'End.Terminate': 'Terminate end event — kills all active process instances',
    'Task': 'Generic task',
    'Task.User': 'User task — requires human interaction',
    'Task.Service': 'Service task — automated, calls a service/API',
    'Task.Send': 'Send task — sends a message',
    'Task.Receive': 'Receive task — waits for a message',
    'Task.Script': 'Script task — executes a script',
    'Task.Business': 'Business rule task — evaluates a business rule',
    'Gateway.Exclusive': 'Exclusive (XOR) gateway — one path taken',
    'Gateway.Parallel': 'Parallel (AND) gateway — all paths taken simultaneously',
    'Gateway.Inclusive': 'Inclusive (OR) gateway — one or more paths taken',
    'Gateway.Event': 'Event-based gateway — waits for first event',
    'Boundary.Timer': 'Timer boundary event (interrupting)',
    'Boundary.Error': 'Error boundary event',
    'Subprocess': 'Collapsed sub-process',
    'Annotation': 'Text annotation artifact',
    'Data.Object': 'Data object',
    'Data.Store': 'Data store reference',
  };
  return descriptions[type] || `BPMN element type: ${type}`;
};
